import { useRef, useState } from 'react';
import { formatDate } from '../dates.ts';
import { visitTypeLabel } from '../visitTypes.ts';
import { useEditReasons } from '../api/facilityPage.ts';
import {
  useEditVisit, useVisit, useVisitVersions, useWithdrawVisit,
  type VisitDetail, type VisitEditBody, type VisitVersion,
} from '../api/visits.ts';
import { Refusal } from './PropertiesPanel.tsx';
import { EarlierVersions, EditedMarker } from './EarlierVersions.tsx';
import { RecordEdit } from './RecordEdit.ts';
import type { EditLike } from '../earlierVersions.ts';
import { numberEdit, numberToSend, numberWords } from '../numberEdit.ts';

const MINUTES = { min: 1, max: 1440 };

const field =
  'mt-1 w-full min-h-[48px] rounded-lg border border-neutral-300 bg-white px-3 ' +
  'text-base text-neutral-900';
const label = 'block text-sm font-semibold text-neutral-700';
const hint = 'mt-1 text-sm text-neutral-600';
const secondary =
  'min-h-[44px] rounded-lg border border-neutral-300 px-3 text-sm font-semibold ' +
  'text-neutral-800 hover:bg-neutral-50 active:bg-neutral-100';
const primary =
  'min-h-[44px] rounded-lg bg-neutral-900 px-4 text-sm font-semibold text-white disabled:bg-neutral-200 disabled:text-neutral-600 disabled:cursor-not-allowed';

const VISIT_TYPES = ['Welfare Visit', 'Initial Site Visit', 'Emergency Visit', 'Collateral Visit', 'Other'];
const HEALTH_FLAGS = ['None', 'Hospitalization', 'Significant Decline', 'Injury', 'Behavioral Incident', 'Other'];

function todayLocal(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * A welfare visit opened from the client's history (125): what was recorded, who visited and who recorded it,
 * every change kept as a version with its reason, and for those who may (tiers 1-2, the person who recorded it,
 * the Director of Social Work) Edit and Withdraw. A withdrawn visit stays here, marked, and stops counting.
 */
export function VisitDetails({ id, onClose, readOnly = false }: { id: string; onClose?: () => void; readOnly?: boolean }) {
  const visit = useVisit(id);
  const [mode, setMode] = useState<'view' | 'edit' | 'withdraw'>('view');
  const [said, setSaid] = useState<string | null>(null);
  const v = visit.data?.visit;
  // Read only for a visit that was edited: the marker and the earlier versions come from it.
  const versions = useVisitVersions(id, (v?.edits ?? 0) > 0);

  if (visit.error) return <div className="mt-2"><Refusal error={visit.error} /></div>;
  if (!v) return <p className="mt-2 text-sm text-neutral-600">Loading the visit…</p>;

  const edits = versions.data ? asEdits(versions.data.versions) : undefined;
  return (
    <section aria-label="Visit details" className="mt-2 space-y-3 rounded-lg border border-neutral-300 bg-panel p-3">
      <dl className="grid gap-x-4 gap-y-1 text-sm sm:grid-cols-2">
        <Fact term="Visited by" value={v.visited_by ?? 'not recorded'} />
        <Fact term="Recorded by" value={v.recorded_by ?? (v.is_migrated ? 'loaded from the tracker' : 'not on record')} />
        {v.duration_minutes !== null && <Fact term="Minutes" value={String(v.duration_minutes)} />}
        {v.appearance && <Fact term="Appearance" value={v.appearance} />}
        {v.mood && <Fact term="Mood" value={v.mood} />}
        {v.affect && <Fact term="Affect" value={v.affect} />}
        {v.cognitive_status && <Fact term="Cognitive status" value={v.cognitive_status} />}
        {v.health_flag && v.health_flag !== 'None' && <Fact term="Of concern" value={v.health_flag} />}
      </dl>
      {/* The note as it stands, ONCE (19 October): earlier versions only under "See earlier versions". */}
      {v.summary && <p className="text-sm whitespace-pre-line text-neutral-800">{v.summary}</p>}
      {v.edits > 0 && <EditedMarker edits={edits} />}
      {v.withdrawn && (
        <p className="rounded-lg bg-neutral-100 px-3 py-2 text-sm font-semibold text-neutral-800">
          Withdrawn: {v.withdrawal_reason}. It stays on record and no longer counts toward any visit owed.
        </p>
      )}

      {said && <p role="status" className="rounded-lg bg-neutral-50 px-3 py-2 text-sm text-neutral-800">{said}</p>}

      {v.edits > 0 && (
        <EarlierVersions edits={edits} first={firstOf(versions.data?.versions)} loading={versions.isLoading}
                         error={versions.error as Error | null} format={visitValue} />
      )}

      {mode === 'view' && !readOnly && (
        <div className="flex flex-wrap gap-2">
          <RecordEdit may={v.may_edit && !v.withdrawn} className={secondary} choices={[
            { key: 'correct', label: 'Correct', onPick: () => { setMode('edit'); setSaid(null); } },
            { key: 'withdraw', label: 'Withdraw: recorded in error', onPick: () => { setMode('withdraw'); setSaid(null); } },
          ]} />
          {onClose && <button type="button" className={secondary} onClick={onClose}>Close</button>}
        </div>
      )}
      {mode === 'edit' && (
        <EditVisit visit={v} onDone={(m) => { setMode('view'); setSaid(m); }} />
      )}
      {mode === 'withdraw' && (
        <WithdrawVisit id={v.id} onDone={(m) => { setMode('view'); setSaid(m); }} />
      )}
    </section>
  );
}

function Fact({ term, value }: { term: string; value: string }) {
  return (
    <div className="flex gap-2">
      <dt className="text-neutral-600">{term}</dt>
      <dd className="font-medium text-neutral-900">{value}</dd>
    </div>
  );
}

/** A visit's (or a site visit's) versions as the shared earlier-versions list reads them. */
export function asEdits(versions: VisitVersion[]): EditLike[] {
  return versions.map((ver) => ({
    version: ver.version, kind: ver.kind, by: ver.recorded_by, at: ver.recorded_at,
    reason: ver.reason_label ?? ver.reason, reason_text: ver.reason_text, changes: ver.changes,
  }));
}

/** Who recorded it first, and when: the version as first recorded. */
export function firstOf(versions: VisitVersion[] | undefined): { by: string | null; at: string | null } | null {
  const v1 = versions?.find((ver) => ver.kind === 'as recorded');
  return v1 ? { by: v1.recorded_by, at: v1.recorded_at } : null;
}

/** How a visit's field reads: its type by label, a date as a date. */
export function visitValue(field: string, value: string | null): string {
  if (value === null || value === '') return 'blank';
  if (field === 'visit_type') return visitTypeLabel(value) ?? value;
  if (field.endsWith('date') || field === 'visited_on') return formatDate(value);
  return value;
}

/** The reason an edit gives: a choice from the shared list (123), and words when it is Other. */
export function ReasonFields({ reason, text, setReason, setText, idPrefix }: {
  reason: string; text: string; setReason: (r: string) => void; setText: (t: string) => void; idPrefix: string;
}) {
  const reasons = useEditReasons();
  const chosen = reasons.data?.reasons.find((r) => r.code === reason);
  return (
    <div className="space-y-3">
      <div>
        <label className={label} htmlFor={`${idPrefix}-reason`}>Why is it being changed?</label>
        <select id={`${idPrefix}-reason`} className={field} value={reason} onChange={(e) => setReason(e.target.value)}>
          <option value="">Choose…</option>
          {reasons.data?.reasons.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
        </select>
      </div>
      {chosen?.needs_text && (
        <div>
          <label className={label} htmlFor={`${idPrefix}-reason-text`}>Say why, in a few words</label>
          <input id={`${idPrefix}-reason-text`} className={field} value={text} maxLength={2000} onChange={(e) => setText(e.target.value)} />
        </div>
      )}
    </div>
  );
}

export function useReasonReady(reason: string, text: string): boolean {
  const reasons = useEditReasons();
  const chosen = reasons.data?.reasons.find((r) => r.code === reason);
  return !!chosen && (!chosen.needs_text || text.trim() !== '');
}

function EditVisit({ visit, onDone }: { visit: VisitDetail; onDone: (m: string | null) => void }) {
  const [form, setForm] = useState({
    visit_date: visit.visit_date,
    visit_type: visit.visit_type,
    location: (visit.at_stay ? 'stay' : 'placement') as 'placement' | 'stay',
    duration_minutes: visit.duration_minutes === null ? '' : String(visit.duration_minutes),
    appearance: visit.appearance ?? '',
    mood: visit.mood ?? '',
    affect: visit.affect ?? '',
    cognitive_status: visit.cognitive_status ?? '',
    health_flag: visit.health_flag ?? 'None',
    summary: visit.summary ?? '',
  });
  const [reason, setReason] = useState('');
  const [reasonText, setReasonText] = useState('');
  const save = useEditVisit(visit.id);
  const sending = useRef(false);
  const reasonReady = useReasonReady(reason, reasonText);
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));
  // F-86: the box's text AND whether the browser could read it (unreadable text reports as ''), and an explicit tick
  // to remove recorded minutes; a blank never erases them on its own (numberEdit.ts)
  const [minutesBad, setMinutesBad] = useState(false);
  const [removeMinutes, setRemoveMinutes] = useState(false);
  const minutesEdit = numberEdit(form.duration_minutes, minutesBad, visit.duration_minutes, MINUTES);
  const minutes = numberToSend(minutesEdit, visit.duration_minutes, removeMinutes);

  const blank = (s: string) => (s.trim() === '' ? null : s.trim());
  const body: VisitEditBody = {
    visit_date: form.visit_date,
    visit_type: form.visit_type,
    duration_minutes: minutes === undefined ? visit.duration_minutes : minutes,
    appearance: blank(form.appearance),
    mood: blank(form.mood),
    affect: blank(form.affect),
    cognitive_status: blank(form.cognitive_status),
    health_flag: form.health_flag,
    summary: blank(form.summary),
    reason,
    ...(reasonText.trim() ? { reason_text: reasonText.trim() } : {}),
    ...(!visit.is_migrated ? { location: form.location } : {}),
  };
  const changed =
    form.visit_date !== visit.visit_date || form.visit_type !== visit.visit_type
    || body.duration_minutes !== visit.duration_minutes || body.appearance !== (visit.appearance ?? null)
    || body.mood !== (visit.mood ?? null) || body.affect !== (visit.affect ?? null)
    || body.cognitive_status !== (visit.cognitive_status ?? null) || form.health_flag !== (visit.health_flag ?? 'None')
    || body.summary !== (visit.summary?.trim() || null)
    || (!visit.is_migrated && form.location !== (visit.at_stay ? 'stay' : 'placement'));
  const ready = changed && reasonReady && form.visit_date !== '' && minutes !== undefined && !save.isPending;

  return (
    <form className="space-y-3 rounded-lg border border-neutral-300 p-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (!ready || sending.current) return;
            sending.current = true;
            save.mutate(body, {
              onSuccess: () => onDone('Saved. The visit as it was is kept as an earlier version.'),
              onError: () => { sending.current = false; },
            });
          }}>
      <h3 className="font-semibold text-neutral-900">Edit the visit</h3>
      <p className={hint}>
        Correct what was recorded. Who visited cannot be changed here: a different visitor is a different visit, so
        withdraw this one and record the right one. Every change is kept, with your reason.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className={label} htmlFor="ve-date">Date of the visit</label>
          <input id="ve-date" type="date" className={field} value={form.visit_date} max={todayLocal()} onChange={set('visit_date')} />
        </div>
        <div>
          <label className={label} htmlFor="ve-type">Type</label>
          <select id="ve-type" className={field} value={form.visit_type} onChange={set('visit_type')}>
            {VISIT_TYPES.map((t) => <option key={t} value={t}>{visitTypeLabel(t)}</option>)}
          </select>
        </div>
        {!visit.is_migrated && (
          <div>
            <label className={label} htmlFor="ve-where">Where</label>
            <select id="ve-where" className={field} value={form.location} onChange={set('location')}>
              <option value="placement">At their placement</option>
              <option value="stay">During a temporary stay</option>
            </select>
            <p className={hint}>Worked out for the date, as when a visit is recorded.</p>
          </div>
        )}
        <div>
          <label className={label} htmlFor="ve-minutes">Minutes <span className="font-normal text-neutral-500">— optional</span></label>
          <input id="ve-minutes" type="number" inputMode="numeric" min={MINUTES.min} max={MINUTES.max} className={field}
                 value={form.duration_minutes} aria-invalid={minutesEdit.kind === 'invalid'}
                 onChange={(e) => {
                   setMinutesBad(e.target.validity.badInput);
                   setRemoveMinutes(false);
                   set('duration_minutes')(e);
                 }} />
          {minutesEdit.kind === 'invalid' && (
            <p className={hint}>Minutes must be {numberWords(MINUTES)}, or left as they were.</p>
          )}
          {minutesEdit.kind === 'clear' && (
            <label className="mt-2 flex min-h-[44px] items-center gap-2 text-sm text-neutral-800" htmlFor="ve-minutes-remove">
              <input id="ve-minutes-remove" type="checkbox" className="size-5 rounded border-neutral-400"
                     checked={removeMinutes} onChange={(e) => setRemoveMinutes(e.target.checked)} />
              Remove the recorded {visit.duration_minutes} minutes from this visit
            </label>
          )}
        </div>
        <div>
          <label className={label} htmlFor="ve-flag">Anything of concern</label>
          <select id="ve-flag" className={field} value={form.health_flag} onChange={set('health_flag')}>
            {HEALTH_FLAGS.map((h) => <option key={h} value={h}>{h}</option>)}
          </select>
        </div>
        <div>
          <label className={label} htmlFor="ve-appearance">Appearance</label>
          <input id="ve-appearance" className={field} value={form.appearance} maxLength={32} onChange={set('appearance')} />
        </div>
        <div>
          <label className={label} htmlFor="ve-mood">Mood</label>
          <input id="ve-mood" className={field} value={form.mood} maxLength={32} onChange={set('mood')} />
        </div>
        <div>
          <label className={label} htmlFor="ve-affect">Affect</label>
          <input id="ve-affect" className={field} value={form.affect} maxLength={32} onChange={set('affect')} />
        </div>
        <div>
          <label className={label} htmlFor="ve-cognitive">Cognitive status</label>
          <input id="ve-cognitive" className={field} value={form.cognitive_status} maxLength={32} onChange={set('cognitive_status')} />
        </div>
      </div>
      <div>
        <label className={label} htmlFor="ve-summary">What happened</label>
        <textarea id="ve-summary" rows={5} className={`${field} py-2 leading-snug`} value={form.summary} onChange={set('summary')} />
      </div>
      <ReasonFields reason={reason} text={reasonText} setReason={setReason} setText={setReasonText} idPrefix="ve" />
      {!changed && <p className={hint}>Nothing has changed yet.</p>}
      {save.error && <Refusal error={save.error} />}
      <div className="flex flex-wrap gap-2">
        <button type="submit" className={primary} disabled={!ready}>{save.isPending ? 'Saving…' : 'Save the change'}</button>
        <button type="button" className={secondary} onClick={() => onDone(null)}>Cancel</button>
      </div>
    </form>
  );
}

function WithdrawVisit({ id, onDone }: { id: string; onDone: (m: string | null) => void }) {
  const [reason, setReason] = useState('');
  const withdraw = useWithdrawVisit(id);
  const sending = useRef(false);
  return (
    <form className="space-y-2 rounded-lg border border-neutral-300 p-3"
          onSubmit={(ev) => {
            ev.preventDefault();
            if (!reason.trim() || sending.current) return;
            sending.current = true;
            withdraw.mutate(reason.trim(), {
              onSuccess: () => onDone('Withdrawn. It stays on record, marked, and stops counting.'),
              onError: () => { sending.current = false; },
            });
          }}>
      <p className="text-sm text-neutral-700">
        For a visit recorded in error. It stays on record, marked as withdrawn with your reason, and stops counting
        toward the four a year. If it met the initial visit, the next visit on or after the appointment meets it, or
        it is owed again.
      </p>
      <label className={label} htmlFor={`vw-why-${id}`}>Why is it being withdrawn?</label>
      <textarea id={`vw-why-${id}`} className={`${field} min-h-[72px] py-2`} value={reason} maxLength={2000}
                onChange={(e) => setReason(e.target.value)} />
      {withdraw.error && <Refusal error={withdraw.error} />}
      <div className="flex gap-2">
        <button type="submit" className={primary} disabled={!reason.trim() || withdraw.isPending}>Withdraw it</button>
        <button type="button" className={secondary} onClick={() => onDone(null)}>Cancel</button>
      </div>
    </form>
  );
}
